/**
 * Voice of X — report definitions + run dispatch endpoints (M1.7 / M1.8).
 *
 * Wire mirror of the BE at
 *   ``ando-agent/src/api/v1/endpoints/voice_reports.py`` (mounted under
 *   ``/api/v1/voice-reports``). All eight FastAPI routes are exposed here:
 *
 *   GET    /voice-reports                  → listVoiceReports
 *   POST   /voice-reports                  → createVoiceReport
 *   GET    /voice-reports/{id}             → getVoiceReport
 *   PATCH  /voice-reports/{id}             → updateVoiceReport
 *   DELETE /voice-reports/{id}             → deleteVoiceReport
 *   POST   /voice-reports/{id}/run         → runVoiceReport
 *   GET    /voice-reports/{id}/runs        → listVoiceReportRuns
 *   POST   /voice-reports/runs/{id}/cancel → cancelVoiceReportRun
 *
 * Field shapes match the BE Pydantic mirrors exactly (``VoiceReportPublic``,
 * ``VoiceReportRunPublic``, …). The rich generated payload returned on a
 * ``success`` run lives on ``VoiceReportRun.content_json`` and conforms to
 * ``packages/core/src/domain/voice_report.ts::ReportNativeSchema`` — parse
 * it with the Zod schema when surfacing it in the viewer.
 *
 * DRY: follows the exact pattern of ``templates.ts`` / ``projects.ts`` —
 * single ``apiClient.{get,post,patch,delete}`` calls, no fancy envelopes,
 * matching response shapes are typed via TS interfaces.
 */

import { apiClient } from "../ApiClient.js";

/**
 * Lifecycle status of a single generation run.
 *
 * Mirrors ``_RunStatusLiteral`` in the BE — DO NOT add states without
 * landing the BE first (the FE badge mapping in ``VoiceReportsList`` /
 * ``RunsTab`` switches on exactly these five values).
 */
export type VoiceReportRunStatus =
  | "pending"
  | "running"
  | "success"
  | "failed"
  | "cancelled";

/** Mirror of ``VoiceReportPublic`` — the report DEFINITION row. */
export interface VoiceReport {
  id: string;
  user_id: string;
  tenant_id: string;
  title: string;
  brief: string;
  /** Free-form dataset filter + sampling preferences (consumed by the pipeline). */
  scope_json: Record<string, unknown>;
  template_id: string | null;
  schedule_cron: string | null;
  /** ISO-8601 timestamps. */
  created_at: string;
  updated_at: string;
}

/**
 * Mirror of ``VoiceReportRunPublic`` — one generation run row.
 *
 * ``content_json`` is the ``ReportNative`` payload populated by the
 * pipeline at completion — ``null`` for pending / running / failed /
 * cancelled rows.
 */
export interface VoiceReportRun {
  id: string;
  report_id: string;
  tenant_id: string;
  status: VoiceReportRunStatus;
  content_json: Record<string, unknown> | null;
  generated_at: string | null;
  error_text: string | null;
  parent_run_id: string | null;
  created_at: string;
}

/** Body of ``POST /voice-reports``. */
export interface CreateVoiceReportInput {
  title: string;
  brief: string;
  scope_json?: Record<string, unknown>;
  template_id?: string | null;
  schedule_cron?: string | null;
}

/** Body of ``PATCH /voice-reports/{id}`` — every field optional. */
export interface UpdateVoiceReportInput {
  title?: string;
  brief?: string;
  scope_json?: Record<string, unknown>;
  template_id?: string | null;
  schedule_cron?: string | null;
}

/** Body of ``POST /voice-reports/{id}/run``. */
export interface RunVoiceReportInput {
  dataset_id: string;
}

/** Returned by ``POST /voice-reports/{id}/run`` — 202 Accepted. */
export interface RunVoiceReportResponse {
  run_id: string;
  status: VoiceReportRunStatus;
}

/** Paginated envelope for ``GET /voice-reports``. */
export interface VoiceReportsListResponse {
  total: number;
  reports: VoiceReport[];
}

/** Paginated envelope for ``GET /voice-reports/{id}/runs``. */
export interface VoiceReportRunsListResponse {
  total: number;
  runs: VoiceReportRun[];
}

// =============================================================================
// Calls
// =============================================================================

export interface ListVoiceReportsParams {
  readonly limit?: number;
  readonly offset?: number;
}

function appendListQs(params?: ListVoiceReportsParams): string {
  if (!params) return "";
  const qs = new URLSearchParams();
  if (typeof params.limit === "number") qs.set("limit", String(params.limit));
  if (typeof params.offset === "number") qs.set("offset", String(params.offset));
  const out = qs.toString();
  return out ? `?${out}` : "";
}

/** GET ``/voice-reports`` — caller's reports, newest first. */
export async function listVoiceReports(
  params?: ListVoiceReportsParams,
): Promise<VoiceReportsListResponse> {
  return await apiClient.get<VoiceReportsListResponse>(
    `/voice-reports${appendListQs(params)}`,
  );
}

/** POST ``/voice-reports`` — create a report definition. */
export async function createVoiceReport(
  input: CreateVoiceReportInput,
): Promise<VoiceReport> {
  return await apiClient.post<VoiceReport>("/voice-reports", input);
}

/** GET ``/voice-reports/{id}`` — single report definition. */
export async function getVoiceReport(reportId: string): Promise<VoiceReport> {
  return await apiClient.get<VoiceReport>(
    `/voice-reports/${encodeURIComponent(reportId)}`,
  );
}

/** PATCH ``/voice-reports/{id}`` — partial update. */
export async function updateVoiceReport(
  reportId: string,
  patch: UpdateVoiceReportInput,
): Promise<VoiceReport> {
  return await apiClient.patch<VoiceReport>(
    `/voice-reports/${encodeURIComponent(reportId)}`,
    patch,
  );
}

/** DELETE ``/voice-reports/{id}`` — hard delete; runs cascade. */
export async function deleteVoiceReport(reportId: string): Promise<void> {
  await apiClient.delete<void>(
    `/voice-reports/${encodeURIComponent(reportId)}`,
  );
}

/** POST ``/voice-reports/{id}/run`` — kick off a generation run. */
export async function runVoiceReport(
  reportId: string,
  input: RunVoiceReportInput,
): Promise<RunVoiceReportResponse> {
  return await apiClient.post<RunVoiceReportResponse>(
    `/voice-reports/${encodeURIComponent(reportId)}/run`,
    input,
  );
}

/** GET ``/voice-reports/{id}/runs`` — run history (newest first). */
export async function listVoiceReportRuns(
  reportId: string,
  params?: ListVoiceReportsParams,
): Promise<VoiceReportRunsListResponse> {
  return await apiClient.get<VoiceReportRunsListResponse>(
    `/voice-reports/${encodeURIComponent(reportId)}/runs${appendListQs(params)}`,
  );
}

/** POST ``/voice-reports/runs/{id}/cancel`` — cancel pending/running. */
export async function cancelVoiceReportRun(
  runId: string,
): Promise<VoiceReportRun> {
  return await apiClient.post<VoiceReportRun>(
    `/voice-reports/runs/${encodeURIComponent(runId)}/cancel`,
    {},
  );
}

// ---------------------------------------------------------------------------
// M3.3 / M3.5 / M3.6 — distribution + diff
// ---------------------------------------------------------------------------

/** Returned by ``POST /voice-reports/runs/{id}/export/pdf``. */
export interface ExportPdfResponse {
  run_id: string;
  pdf_minio_key: string;
  /** ``"ready"`` ⇒ re-used a previous export; ``"accepted"`` ⇒ fresh render. */
  status: "accepted" | "ready";
}

/** POST ``/voice-reports/runs/{id}/export/pdf`` — render to PDF (M3.3). */
export async function exportVoiceReportPdf(
  runId: string,
): Promise<ExportPdfResponse> {
  return await apiClient.post<ExportPdfResponse>(
    `/voice-reports/runs/${encodeURIComponent(runId)}/export/pdf`,
    {},
  );
}

/** Body of ``POST /voice-reports/runs/{id}/distribute/email``. */
export interface DistributeEmailInput {
  recipients: string[];
  cc?: string[];
  subject?: string | null;
  message?: string | null;
}

/** Returned by ``POST /voice-reports/runs/{id}/distribute/email``. */
export interface DistributeEmailResponse {
  run_id: string;
  sent_count: number;
  failed_count: number;
  pdf_minio_key: string;
}

/** POST ``/voice-reports/runs/{id}/distribute/email`` — email send (M3.5). */
export async function distributeVoiceReportEmail(
  runId: string,
  input: DistributeEmailInput,
): Promise<DistributeEmailResponse> {
  return await apiClient.post<DistributeEmailResponse>(
    `/voice-reports/runs/${encodeURIComponent(runId)}/distribute/email`,
    input,
  );
}

/**
 * Mirror of the BE ``KpiDeltaPublic`` row. ``delta_pct`` is ``null`` when
 * ``prev == 0`` and ``curr != 0`` (mathematically undefined — UI should
 * render "new" instead of a percentage).
 */
export interface KpiDelta {
  prev: number | null;
  curr: number | null;
  delta_pct: number | null;
}

/** Mirror of the BE ``RunDiffResponse`` — semantic timeline source data. */
export interface RunDiff {
  categories_added: string[];
  categories_removed: string[];
  /** ``[prev_name, curr_name]`` pairs detected via Jaro-Winkler ≥ 0.85. */
  categories_renamed: Array<[string, string]>;
  /** ``{category_name: delta}`` — positive ⇒ more positive sentiment. */
  sentiment_shifts: Record<string, number>;
  kpi_deltas: Record<string, KpiDelta>;
  recommendations_added: string[];
  recommendations_dropped: string[];
}

/**
 * GET ``/voice-reports/{id}/runs/{run_id}/diff/{prev_run_id}`` — semantic
 * diff between two finished runs of the SAME report (M3.6).
 */
export async function getVoiceReportRunDiff(
  reportId: string,
  runId: string,
  prevRunId: string,
): Promise<RunDiff> {
  return await apiClient.get<RunDiff>(
    `/voice-reports/${encodeURIComponent(reportId)}/runs/${encodeURIComponent(
      runId,
    )}/diff/${encodeURIComponent(prevRunId)}`,
  );
}
